var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { createAlphaFiSDK } from '../utils/networkUtils';
import { useNetworkString } from './NetworkContext';
import { normalizeCoinType, wbtc_layer0_coinType, wbtc_wormhole_coinType, } from '../utils/coinDisplayUtils';
import { useAdminAddress } from './AdminAddressContext';
import { getSuiClient } from '../utils/client';
/**
 * Give-up limit for reading back a transaction the user just made.
 *
 * This is not a wait. waitForTransaction reads straight away and returns as soon as the
 * transaction can be read; the limit only stops us holding a digest forever if the node
 * never serves it.
 */
const TX_READ_TIMEOUT_MS = 20000;
/** The balances map keeps SUI under the padded coin type and under this short alias. */
const SUI_COIN_TYPE_SHORT = '0x2::sui::SUI';
const SUI_COIN_TYPE = normalizeCoinType(SUI_COIN_TYPE_SHORT);
/** BigInt(0), not 0n — tsconfig targets es6, which has no BigInt literals. */
const ZERO = BigInt(0);
const BalanceContext = createContext({
    walletBalancesMap: undefined,
    addressBalancesMap: undefined,
    isLoading: false,
    refetch: () => { },
});
// Reads wallet balances via the SDK's GraphQL reader (not JSON-RPC). Each balance
// is the combined total — address-balance accumulator + owned Coin<T> objects —
// so funds held in the address balance are included.
export function getWalletCoins(userAddress, sdk) {
    return __awaiter(this, void 0, void 0, function* () {
        try {
            // Map<normalizedCoinType, totalBalance>
            const balances = yield sdk.getWalletBalances(userAddress);
            const resMap = new Map();
            balances.forEach((totalBalance, coinType) => {
                const normalized = normalizeCoinType(coinType);
                if (normalized ===
                    '0x0000000000000000000000000000000000000000000000000000000000000002::sui::SUI') {
                    resMap.set(normalized, totalBalance);
                    resMap.set('0x2::sui::SUI', totalBalance);
                    return;
                }
                // WBTC LayerZero (Black color)
                if (normalized === wbtc_layer0_coinType) {
                    resMap.set(wbtc_layer0_coinType, totalBalance);
                    return;
                }
                // WBTC Wormhole (Orange color)
                if (normalized === wbtc_wormhole_coinType) {
                    resMap.set(wbtc_wormhole_coinType, totalBalance);
                    return;
                }
                resMap.set(normalized, totalBalance);
            });
            return resMap;
        }
        catch (error) {
            console.error('Error fetching tokenBalances!', error);
        }
    });
}
/**
 * Same rule as @mysten/sui's normalizeSuiAddress: lower case, then pad to 32 bytes.
 * Written here rather than imported because it only compares two addresses.
 */
function normalizeAddress(address) {
    const lowerCase = address.toLowerCase();
    const withoutPrefix = lowerCase.startsWith('0x') ? lowerCase.slice(2) : lowerCase;
    return `0x${withoutPrefix.padStart(64, '0')}`;
}
/**
 * What a transaction moved for one address, paired with what the reader said beforehand.
 *
 * `snapshotAtEvent` must be the snapshot held when the transaction was reported: it was
 * fetched before that, so it cannot already contain the transaction. Balance changes cover
 * every address a transaction touched, so everyone else's are dropped here.
 */
function buildPendingTransaction(changes, owner, snapshotAtEvent) {
    const deltas = new Map();
    const ownerAddress = normalizeAddress(owner);
    changes.forEach((change) => {
        var _a;
        if (normalizeAddress(change.address) !== ownerAddress)
            return;
        const amount = BigInt(change.amount);
        if (amount === ZERO)
            return;
        const coinType = normalizeCoinType(change.coinType);
        const total = BigInt((_a = deltas.get(coinType)) !== null && _a !== void 0 ? _a : '0') + amount;
        deltas.set(coinType, total.toString());
    });
    if (deltas.size === 0)
        return undefined;
    const preTxBalances = new Map();
    deltas.forEach((_delta, coinType) => {
        var _a;
        preTxBalances.set(coinType, (_a = snapshotAtEvent.get(coinType)) !== null && _a !== void 0 ? _a : '0');
    });
    return { deltas, preTxBalances };
}
/**
 * The balances to show: the reader's snapshot, plus a pending transaction's deltas while
 * the reader still has not seen it.
 *
 * Decided one coin at a time. A coin the reader has moved past — because of this
 * transaction, or anything else that touched it — has its delta dropped and the reader's
 * own number shown; the rest stay held. Deciding them together would be wrong in a way
 * that shows up constantly: every transaction pays gas, so SUI is in every pending
 * transaction, and an earlier transaction's gas reaching the reader would throw away the
 * hold on a coin the reader demonstrably has not reached — putting a just-borrowed token
 * back to its old balance, which is the bug this is all for.
 *
 * A coin still sitting at its pre-transaction value cannot already include its own delta,
 * which is what makes adding it safe, and that argument is per coin as well.
 */
function applyPendingTransaction(snapshot, pending) {
    if (!pending)
        return { balances: snapshot, pending: undefined };
    const balances = new Map(snapshot);
    const heldDeltas = new Map();
    const heldPreTxBalances = new Map();
    pending.deltas.forEach((delta, coinType) => {
        var _a, _b, _c;
        const preTxBalance = (_a = pending.preTxBalances.get(coinType)) !== null && _a !== void 0 ? _a : '0';
        if (((_b = snapshot.get(coinType)) !== null && _b !== void 0 ? _b : '0') !== preTxBalance)
            return;
        heldDeltas.set(coinType, delta);
        heldPreTxBalances.set(coinType, preTxBalance);
        // A balance can never go below zero.
        const total = BigInt((_c = balances.get(coinType)) !== null && _c !== void 0 ? _c : '0') + BigInt(delta);
        const balance = (total > ZERO ? total : ZERO).toString();
        balances.set(coinType, balance);
        if (coinType === SUI_COIN_TYPE) {
            balances.set(SUI_COIN_TYPE_SHORT, balance);
        }
    });
    if (heldDeltas.size === 0)
        return { balances: snapshot, pending: undefined };
    return { balances, pending: { deltas: heldDeltas, preTxBalances: heldPreTxBalances } };
}
export const BalanceProvider = ({ children }) => {
    const network = useNetworkString();
    const { effectiveAddress, isAdminMode } = useAdminAddress();
    const [walletBalancesMap, setWalletBalancesMap] = useState(() => {
        const address = effectiveAddress;
        if (!address)
            return undefined;
        const cachedNetwork = network || 'mainnet';
        const cacheKey = `wallet-balances-${cachedNetwork}-${address}`;
        const cached = localStorage.getItem(cacheKey);
        if (cached) {
            try {
                return new Map(JSON.parse(cached));
            }
            catch (_e) {
                return undefined;
            }
        }
        return undefined;
    });
    const [addressBalancesMap, setAddressBalancesMap] = useState(undefined);
    const [isLoading, setIsLoading] = useState(false);
    const prevEffectiveAddressRef = useRef(undefined);
    // What the reader last told us, readable outside render. Kept apart from what is shown:
    // a transaction's deltas are added on top when the balances are handed to the app, and
    // never written back in here.
    const snapshotRef = useRef(undefined);
    // The transaction the reader has not caught up with, if any. One at a time: a second
    // transaction replaces it, and the first one's coins fall back to the reader's numbers.
    const pendingTransactionRef = useRef(undefined);
    // Id of the newest fetch. The interval fires every 5s whether the last one finished or
    // not, and one fetch is several GraphQL round trips, so a slow reply can arrive after a
    // newer one; a fetch that is no longer the newest drops its result.
    const balanceWriteIdRef = useRef(0);
    const commitWalletBalances = useCallback((balances) => {
        setWalletBalancesMap(balances);
        if (network && effectiveAddress) {
            const cacheKey = `wallet-balances-${network}-${effectiveAddress}`;
            localStorage.setItem(cacheKey, JSON.stringify(Array.from(balances.entries())));
        }
    }, [effectiveAddress, network]);
    const clearBalanceState = useCallback(() => {
        snapshotRef.current = undefined;
        pendingTransactionRef.current = undefined;
        balanceWriteIdRef.current += 1;
        setWalletBalancesMap(undefined);
    }, []);
    // Clear stale balances during render when the address changes. Child components
    // (e.g. App's useWalletTracker) run useEffect before this provider's effects
    // (React: inner effects before outer), so clearing only in useEffect would leave
    // one frame where children still see the previous wallet's map.
    //
    // Only state is reset here — a render body has to stay pure. The refs are cleared in the
    // address-change effect below, which covers the same two paths.
    const renderSyncedAddressRef = useRef(effectiveAddress);
    if (renderSyncedAddressRef.current !== effectiveAddress) {
        renderSyncedAddressRef.current = effectiveAddress;
        if (!effectiveAddress) {
            if (walletBalancesMap !== undefined) {
                setWalletBalancesMap(undefined);
            }
            setIsLoading(false);
        }
        else {
            const switched = prevEffectiveAddressRef.current !== undefined &&
                prevEffectiveAddressRef.current !== effectiveAddress;
            if (switched) {
                if (walletBalancesMap !== undefined) {
                    setWalletBalancesMap(undefined);
                }
                setIsLoading(true);
            }
        }
    }
    const fetchBalances = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        if (!effectiveAddress || !network)
            return;
        const writeId = ++balanceWriteIdRef.current;
        const isNewestWrite = () => writeId === balanceWriteIdRef.current;
        setIsLoading(true);
        try {
            const sdk = createAlphaFiSDK(network);
            const balances = yield getWalletCoins(effectiveAddress, sdk);
            // Extra call needed — the SDK only returns total balance, not the address-accumulator vs coin-object breakdown.
            let addressBalances;
            try {
                const suiClient = getSuiClient(network);
                const rpcBalances = yield suiClient.listBalances({ owner: effectiveAddress });
                const addrMap = new Map();
                rpcBalances.balances.forEach((b) => {
                    if (b.addressBalance && BigInt(b.addressBalance) > 0) {
                        addrMap.set(normalizeCoinType(b.coinType), '1');
                    }
                });
                addressBalances = addrMap;
            }
            catch (_rpcErr) {
                addressBalances = new Map(); // empty = no address-balance funds, address balance guard allows the tx
            }
            // A newer fetch has written since this one started. Checked before both writes
            // below, so an older reply cannot land on either map.
            if (!isNewestWrite())
                return;
            setAddressBalancesMap(addressBalances);
            // getWalletCoins returns undefined when the read fails, and one failed page kills
            // the whole paginated call. Writing that would wipe every balance to zero — which
            // reads as "no funds" everywhere and, on the Repay tab, silently switches the pay
            // token. Keep the last known balances and let the next fetch correct them.
            if (balances) {
                const shown = applyPendingTransaction(balances, pendingTransactionRef.current);
                pendingTransactionRef.current = shown.pending;
                snapshotRef.current = balances;
                commitWalletBalances(shown.balances);
            }
            // Only cache last-connected-address for real wallet connections, not admin view
            if (!isAdminMode) {
                localStorage.setItem('last-connected-address', effectiveAddress);
                localStorage.setItem('last-network', network);
            }
        }
        catch (error) {
            console.error('Error fetching balances:', error);
        }
        finally {
            // Leave the flag set if a newer fetch is still running — it clears it itself.
            if (isNewestWrite())
                setIsLoading(false);
        }
    }), [commitWalletBalances, effectiveAddress, isAdminMode, network]);
    /**
     * Show a transaction the user just made, then refresh from the reader.
     *
     * The wallet only reports that the transaction executed, so the amounts are read back
     * from the transaction itself. `snapshotAtEvent` and `addressAtEvent` are taken when the
     * transaction is reported, before any of this awaits.
     */
    const applyTransactionBalances = useCallback((digest, snapshotAtEvent, addressAtEvent) => __awaiter(void 0, void 0, void 0, function* () {
        var _a, _b, _c, _d;
        // With no snapshot yet there is nothing to add the deltas to, and the fetch below is
        // the first balances the app will have anyway.
        if (digest && network && snapshotAtEvent && addressAtEvent) {
            try {
                const suiClient = getSuiClient(network);
                const txResult = yield suiClient.waitForTransaction({
                    digest,
                    include: { balanceChanges: true },
                    timeout: TX_READ_TIMEOUT_MS,
                });
                const changes = (_c = (_b = ((_a = txResult.Transaction) !== null && _a !== void 0 ? _a : txResult.FailedTransaction)) === null || _b === void 0 ? void 0 : _b.balanceChanges) !== null && _c !== void 0 ? _c : [];
                const pending = buildPendingTransaction(changes, addressAtEvent, snapshotAtEvent);
                // Reading the transaction can take until TX_READ_TIMEOUT_MS, and the user can
                // switch wallets in that time — this callback keeps the address it started with.
                if (pending && renderSyncedAddressRef.current === addressAtEvent) {
                    // Worked out against the snapshot in hand, which also settles the case where
                    // the reader caught up while the transaction was being read: the deltas are
                    // dropped rather than added to numbers that already contain them.
                    const snapshot = (_d = snapshotRef.current) !== null && _d !== void 0 ? _d : snapshotAtEvent;
                    const shown = applyPendingTransaction(snapshot, pending);
                    pendingTransactionRef.current = shown.pending;
                    commitWalletBalances(shown.balances);
                }
            }
            catch (error) {
                // Could not read the transaction back (node does not serve balance changes,
                // timed out, network error). The fetch is then all we have, which is what
                // happened before this path existed.
                console.error('Error reading balance changes after transaction:', error);
            }
        }
        // Coins the transaction did not touch still need refreshing. Skipped once the app has
        // moved to another wallet — that wallet's own fetch is the one that should win.
        if (renderSyncedAddressRef.current !== addressAtEvent)
            return;
        yield fetchBalances();
    }), [commitWalletBalances, fetchBalances, network]);
    useEffect(() => {
        if (!effectiveAddress) {
            prevEffectiveAddressRef.current = undefined;
            clearBalanceState();
            setAddressBalancesMap(undefined);
            setIsLoading(false);
            return;
        }
        // Drop stale balances immediately when the address changes so dependents (e.g. wallet
        // analytics) do not read the previous account's map while the new fetch is in flight.
        const switchedAccount = prevEffectiveAddressRef.current !== undefined &&
            prevEffectiveAddressRef.current !== effectiveAddress;
        prevEffectiveAddressRef.current = effectiveAddress;
        if (switchedAccount) {
            clearBalanceState();
            setIsLoading(true);
        }
        fetchBalances();
        const interval = setInterval(fetchBalances, 5000);
        return () => clearInterval(interval);
    }, [clearBalanceState, fetchBalances, effectiveAddress]);
    // Every transaction handler reports success with this event, and every transaction
    // changes wallet balances. Show it straight away instead of waiting for the next poll —
    // that wait is why a token that was just borrowed still showed its old wallet balance in
    // the repay input and its MAX button.
    useEffect(() => {
        if (!effectiveAddress)
            return;
        const handleTransactionSuccess = (event) => {
            var _a;
            const digest = (_a = event.detail) === null || _a === void 0 ? void 0 : _a.digest;
            // Read before anything awaits: this snapshot was fetched before the transaction was
            // reported, so it cannot already contain it.
            void applyTransactionBalances(digest, snapshotRef.current, effectiveAddress);
        };
        document.addEventListener('transactionSuccess', handleTransactionSuccess);
        return () => document.removeEventListener('transactionSuccess', handleTransactionSuccess);
    }, [applyTransactionBalances, effectiveAddress]);
    return (_jsx(BalanceContext.Provider, { value: { walletBalancesMap, addressBalancesMap, isLoading, refetch: fetchBalances }, children: children }));
};
export const useBalances = () => {
    const context = useContext(BalanceContext);
    if (!context) {
        throw new Error('useBalances must be used within a BalanceProvider');
    }
    return context;
};
